/**
 * Куда вести человека сразу после регистрации/входа.
 *
 * Тот, у кого подписки НИКОГДА не было, попадал в пустой кабинет и должен был
 * сам догадаться нажать «Подключить». Теперь такой человек уходит прямо на
 * витрину `/buy` с выбранным пробником за 3 ₽ — экран активации это и есть
 * следующий шаг его пути, а не отдельный раздел где-то в меню.
 *
 * Признак «была ли подписка» ровно один и живёт на бэкенде: `access` в ответе
 * `GET /vpn/overview` — `null` только у того, у кого нет ни одной записи
 * `vpn_access` (VpnController::actionOverview). Погашенная или просроченная
 * подписка `access` не обнуляет, и такой человек по-прежнему идёт в кабинет.
 *
 * Молчащий ответ (сеть отвалилась, бэкенд ответил ошибкой) НЕ считается
 * «подписки не было»: `overview` остаётся `null`, и человек идёт в кабинет —
 * прежним путём. Увести на витрину оплаты того, кто уже платит, из-за сетевой
 * ошибки хуже, чем не показать ему предложение.
 */
import type { RouteLocationRaw } from 'vue-router'

import { takeAfterLogin } from '@/modules/auth/utils/cancelScope'
import { getPostAuthRouteOverride } from '@/modules/auth/utils/postAuthOverride'
import { useVpnStore } from '@/modules/vpn/store/vpnStore'
import { hasFiveGIntent } from '@/modules/vpn/utils/fiveGIntent'
import { readStoredInvite } from '@/modules/vpn/utils/poolInvite'
import { isTradeInReturnPath } from '@/modules/vpn/utils/tradeinClaim'

export { setPostAuthRouteResolver } from './postAuthOverride'

export async function resolvePostAuthRoute(
  options: { tradeInReturn?: string } = {},
): Promise<RouteLocationRaw> {
  // sprint-17 — подмена цели (i5g.org), см. postAuthOverride.ts.
  const override = getPostAuthRouteOverride()
  if (override) return override()

  const vpnStore = useVpnStore()

  // Вход с экрана отмены по ссылке из письма (ограниченная сессия): человек
  // нажал «Друзья», «Подарки», оплату со скидкой или «Войти в кабинет» —
  // ведём ровно туда, куда он шёл.
  const afterLogin = takeAfterLogin()
  if (afterLogin !== null) return afterLogin

  // Возврат разрешает только вход с явно выбранной страницы переноса.
  // Сохранённые claim/resume живут дольше входа и не должны перехватывать
  // главную или вести в старую сессию вместо той, которую открыли сейчас.
  if (isTradeInReturnPath(options.tradeInReturn)) return { path: options.tradeInReturn }

  // Sprint 17 ч.2 — пришёл за ключом для i5G: ключ выдаёт /5g, а не витрина.
  if (hasFiveGIntent()) return { name: 'fiveg' }

  // Приглашение в общую подписку сильнее витрины: человек пришёл по чужой
  // ссылке принимать чужую подписку, а не покупать свою. Приём делает
  // «Главная» по этой же записи (VpnDashboardPage), поэтому ведём туда —
  // и делаем это ДО чтения overview, чтобы не гонять лишний запрос.
  if (readStoredInvite() !== null) return { name: 'dashboard' }

  if (!vpnStore.overview) await vpnStore.fetchOverview()

  if (vpnStore.overview !== null && vpnStore.overview.access === null) {
    // `period=trial3` — тот же оффер входа, что и на «Главной»: витрина умеет
    // предвыбирать период из query (BuyPage.onMounted), и своего механизма для
    // этого заводить не нужно.
    return { name: 'buy', query: { period: 'trial3' } }
  }

  return { name: 'dashboard' }
}
